<template>
  <section class="bbb-section">
    <section-header :title="title" />
    <div class="sub-header">
      <div class="dropdown-container">
        <div class="dropdown-block">
          <span>{{ $t('bestBuyBanners.title') }} </span>
          <dropdown-white
            v-if="bestBuyCountries"
            class="countries"
            :translation-prefix="'countries.'"
            :items="bestBuyCountries"
            :selected-item="selectedBestBuyCountry"
            @select="selectCountry"
          />
        </div>

        <div
          v-if="cities.length > 2"
          class="dropdown-block"
        >
          <span>{{ $t('bestBuyBanners.cityTitle') }} </span>
          <dropdown-white
            v-if="cities"
            class="cities"
            :items="cities"
            :label="getCityName(city)"
            :selected-item="city"
            @select="selectCity"
          />
        </div>
      </div>

      <div v-if="!isMobile">
        <a
          v-if="everyMundoLink.text"
          class="section-link"
          :href="everyMundoLink.href"
          :target="everyMundoLink.target"
        >
          {{ everyMundoLink.text }}
        </a>

        <a
          v-if="destinationPageLink.text"
          class="section-link"
          :href="destinationPageLink.href"
          :target="destinationPageLink.target"
        >
          {{ destinationPageLink.text }}
        </a>
      </div>
    </div>

    <carousel
      ref="slider"
      :navigation-enabled="isDesktop"
      :navigation-next-label="''"
      :navigation-prev-label="''"
      :pagination-enabled="false"
      :per-page-custom="[
        [320, 1],
        [768, 2],
        [992, 3]
      ]"
      :scroll-per-page="true"
      :navigate-to="navigateToSlide"
      @pageChange="onSlideChange"
    >
      <slide
        v-for="(banner, index) in bannersByCity"
        :id="`bbb-${index}`"
        :key="index"
      >
        <best-buy-banner
          :banner="banner"
          :orig-dest-labels="origDestLabels"
          :orig-dest-data="origDestData"
          :selected-country="selectedBestBuyCountry"
          :selected-lang="selectedLang"
        />
      </slide>
    </carousel>

    <slider-paginator
      v-if="showPaginator"
      ref="sliderPaginatorRef"
      :items="bannersByCity"
      :page="currentPage"
      :dot-count="6"
      :group-slide="true"
      @paginatorNavigate="paginatorNavigate"
    />
  </section>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import { Carousel, Slide } from 'vue-carousel'
  import BBBService from 'services/AirBaltic/BBB'
  import SliderPaginator from 'components/Elements/SliderPaginator'
  import uniq from 'lodash-es/uniq'
  const DropdownWhite = () =>
    import(
      /* webpackChunkName: "dropdown-white" */ 'components/Dropdown/DropdownWhite.vue'
    )
  const BestBuyBanner = () =>
    import(
      /* webpackChunkName: "best-buy-banner" */ 'containers/Pages/IndexPage/BestBuyBannerItem.vue'
    )
  const SectionHeader = () =>
    import(
      /* webpackChunkName: "index-section-header" */ 'containers/Pages/IndexPage/SectionHeader.vue'
    )

  import * as Sentry from '@sentry/vue'

  export default {
    components: {
      Carousel,
      Slide,
      SliderPaginator,
      DropdownWhite,
      BestBuyBanner,
      SectionHeader
    },
    props: {
      banners: {
        type: Array,
        required: true
      },
      bestBuyCountries: {
        type: Array,
        required: true
      },
      title: {
        type: String,
        default: ''
      },
      listName: {
        type: String,
        default: 'main'
      },
      destinationPageLink: {
        type: Object,
        required: true
      },
      everyMundoLink: {
        type: Object,
        required: true
      }
    },
    data() {
      return {
        country: '',
        city: 'all',
        currentPage: 0,
        navigateToSlide: 0
      }
    },
    computed: {
      ...mapGetters('device', ['isDesktop', 'isTablet', 'isMobile']),
      ...mapState('main', ['selectedCountry', 'selectedLang']),
      ...mapState('indexPage/bestBuyBanners', {
        origDestLabels: 'labels'
      }),
      ...mapState('eHorizonSearchForm', ['origDestData']),
      selectedBestBuyCountry() {
        return this.bestBuyCountries.find((element) => element.code === this.country)
          ? this.country
          : 'ZZ'
      },
      cities() {
        const cities = [{ name: this.$t('reusable.showAll'), code: 'all' }]

        const cityList = this.banners.map((item) => item.orig)
        const cityNameList = []
        uniq(cityList).forEach((item) => {
          cityNameList.push({ name: this.getCityName(item), code: item })
        })

        cityNameList.sort((a, b) => a.name.localeCompare(b.name))

        return [cities, ...cityNameList]
      },
      bannersByCity() {
        if (this.city === 'all') {
          return this.banners
        }

        return this.banners.filter((item) => item.orig === this.city)
      },
      showPaginator() {
        if (this.isDesktop) {
          return this.bannersByCity.length > 3
        }
        if (this.isTablet) {
          return this.bannersByCity.length > 2
        }

        return true
      }
    },
    created() {
      this.country = this.selectedCountry
    },
    methods: {
      getCityName(orig) {
        if (orig === 'all') {
          return this.$t('reusable.showAll')
        }

        let origDest = this.origDestData[`${orig}A`] ?? this.origDestData[`${orig}C`]

        if (!origDest) {
          Sentry.captureException('No city found in origDestData with code: ' + orig)
          return ''
        }

        return origDest?.city
      },
      selectCity(selected) {
        this.city = selected
        this.$nextTick(() => {
          this.onSlideChange(0)
        })
      },
      selectCountry(selected) {
        this.country = selected
        this.city = 'all'
        this.closeModal()

        new BBBService()
          .setSinglePos(selected, this.listName)
          .then((response) => {
            this.$store.commit(`indexPage/bestBuyBanners/setBestBuyBanners`, response)

            this.$nextTick(() => {
              this.onSlideChange(0)
            })
          })
          .catch(() =>
            this.$store.commit(`indexPage/bestBuyBanners/setBestBuyBanners`, [])
          )
          .finally(() => {
            if (this.$refs.slider) {
              this.$refs.slider.goToPage(0)
            }
          })
      },
      closeModal() {
        this.$store.dispatch('main/closeModal')
      },
      onSlideChange(page) {
        this.currentPage = page
        this.navigateToSlide = page

        if (this.$refs.sliderPaginatorRef) {
          this.$refs.sliderPaginatorRef.onSlideChange(page)
        }
      },
      paginatorNavigate(page) {
        this.navigateToSlide = page
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';

  ::v-deep .section-header {
    margin-bottom: 12px !important;

    @include bp-mf-desktop {
      margin-bottom: 22px !important;
    }
  }

  .sub-header {
    line-height: 24px;
    margin-bottom: 16px;
    position: relative;

    @include bp-mf-tablet {
      display: flex;
      justify-content: space-between;
    }

    .countries,
    .cities {
      display: inline-block;
    }

    .dropdown-block {
      font-size: em(16);
      position: relative;
      margin-bottom: 16px;

      &:last-child {
        margin-right: 0;
      }

      @include bp-mf-tablet {
        margin-bottom: 0;
        margin-right: 16px;
        display: inline-block;
      }
    }

    ::v-deep .dropdown-field {
      font-weight: 500;
      color: $blue-medium;
      padding-right: 20px;

      &:before {
        content: none !important;
      }

      &:after {
        content: $icon-arrow-right-thin-medium;
        @include icon-font-thin();
        margin-left: 8px;
        transform: rotate(90deg);
        font-size: 12px;
        line-height: 24px;
        font-weight: 700;
        color: $blue-medium;

        @include bp-mf-tablet {
          line-height: inherit;
          font-size: 14px;
        }
      }
    }

    ::v-deep .dropdown-list {
      z-index: 2;
      position: absolute;
      top: 35px;
      left: 0;
      width: 256px;
      max-height: 275px;
      background: white;
      box-shadow: 0 4px 16px 0 rgba($blue-dark, 0.12);

      li {
        line-height: 55px;
        padding-left: 16px;

        &.selected {
          background-color: $gray-marble;

          a {
            color: $blue-medium;
            font-weight: 500;
          }
        }

        a {
          color: $blue-dark;
        }
      }
    }
  }

  ::v-deep .VueCarousel-inner {
    @include bp-mf-tablet {
      position: relative;
      margin-left: -20px;
      zoom: 1;
    }
  }

  ::v-deep .VueCarousel-slide {
    @include bp-mf-tablet {
      padding-left: 20px;
      max-width: 50%;
    }

    @include bp-mf-desktop {
      max-width: 33.3%;
    }
  }

  ::v-deep .VueCarousel-navigation .VueCarousel-navigation-button {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    box-shadow: 0 4px 16px 0 rgba($blue-dark, 0.2);
    background-color: white;

    &::before {
      @include icon-font-thin();
      color: $blue-medium;
      font-size: em(18);
      content: $icon-arrow-right-thin-medium;
    }

    &.VueCarousel-navigation-prev {
      left: 30px;

      @media (max-width: 1200px) and (min-width: 992px) {
        left: 45px;
      }

      &::before {
        transform: rotate(180deg);
      }
    }

    &.VueCarousel-navigation-next {
      right: 30px;

      @media (max-width: 1200px) and (min-width: 992px) {
        right: 45px;
      }
    }

    &.VueCarousel-navigation--disabled {
      opacity: 1 !important;

      &::before {
        opacity: 0.2;
      }
    }
  }

  .progress-bar {
    height: 12px;
    position: relative;
    margin-top: 24px;
    border-radius: 6px;
    background-color: #dfe2e6;

    @include bp-mf-tablet {
      margin-top: 28px;
    }

    @include bp-mf-desktop {
      margin-top: 40px;
    }

    .progress-actual {
      position: absolute;
      height: 12px;
      border-radius: 6px;
      background-color: $blue-medium;
      transition: 0.1s linear;
    }
  }

  .section-link {
    &:not(:last-child) {
      margin-right: 30px;
    }
  }
</style>
